/**
 * Formulario de **precios del cotizador de puertas**.
 *
 * Réplica de las 3 tarjetas del SSR (`templates/admin_precios_puertas.html`), con
 * un cambio de UX **deliberado y aprobado por la feature #58**: en vez de los 3
 * botones de guardado parcial del SSR, hay **un único botón "Guardar"** que envía
 * las 18 claves juntas en un solo `PUT`. Es seguro e idempotente porque el backend
 * hace merge (`guardar_precios`), y evita el problema del SSR de tener 3 estados
 * de guardado independientes.
 *
 * Patrón de formulario complejo del repo (como `features/orders/OrderForm`):
 * **estado controlado con `useState` + validación Zod en el submit** (NO se
 * introduce `react-hook-form`/`useFieldArray`, que no están en el repo). Los
 * arrays dinámicos se gestionan en el estado con índices (ver los editores).
 *
 * Presentacional + validación local: NO hace `fetch` ni decide permisos. Recibe
 * la config inicial, el estado de envío y el error de la API desde la pantalla,
 * que posee los hooks de datos. Construye el body con `formStateToBody` (descartes
 * + ordenamiento por `hasta`, sin `success`).
 */
import { useEffect, useMemo, useState } from "react";

import { Button, Card, CardContent, CardHeader, CardTitle, Input } from "@/components/ui";

import { adminErrorMessage } from "../format";
import {
  ESCALAR_LABEL,
  SECCION_DOBLES_DERECHA,
  SECCION_DOBLES_IZQUIERDA,
  SECCION_LAMINA,
  TABLAS_CALIBRE,
  TABLA_SIMPLE_TITLE,
  type PrecioEscalarKey,
} from "../precios.constants";
import type { PreciosConfig, UpdatePreciosBody } from "../precios.types";
import {
  configToFormState,
  formStateToBody,
  type CombinadaRow,
  type DoblesRow,
  type PreciosFormState,
  type TablaCalibreState,
  type TablaRow,
} from "../precios.transform";
import { validatePreciosForm, type PreciosFieldErrors } from "../precios.schemas";
import { CombinadaMedidasEditor } from "./CombinadaMedidasEditor";
import { DoblesCalibresEditor } from "./DoblesCalibresEditor";
import { TablaCalibreEditor } from "./TablaCalibreEditor";
import { TablaSimpleEditor } from "./TablaSimpleEditor";

interface PreciosFormProps {
  /** Config tal cual la devuelve el GET; siembra y resiembra el estado. */
  config: PreciosConfig;
  /** Estado de envío (deshabilita el botón y muestra "Guardando…"). */
  isSubmitting: boolean;
  /** `true` justo tras un guardado correcto (para el aviso de éxito). */
  isSaved: boolean;
  /** Error de la API tras enviar (se traduce a mensaje legible). */
  apiError: unknown;
  /** Envía las 18 claves del form (ya validadas y saneadas). */
  onSubmit: (body: UpdatePreciosBody) => void;
}

export function PreciosForm({
  config,
  isSubmitting,
  isSaved,
  apiError,
  onSubmit,
}: PreciosFormProps) {
  const initialState = useMemo(() => configToFormState(config), [config]);
  const [state, setState] = useState<PreciosFormState>(initialState);
  const [fieldErrors, setFieldErrors] = useState<PreciosFieldErrors>({});
  const [formError, setFormError] = useState<string | null>(null);

  // Tras una recarga (invalidación post-guardado), `config` cambia: resembramos el
  // estado con los valores persistidos (paridad con el "guardar + recargar" SSR).
  useEffect(() => {
    setState(initialState);
  }, [initialState]);

  function setEscalar(key: PrecioEscalarKey, value: string) {
    setState((prev) => ({
      ...prev,
      escalares: { ...prev.escalares, [key]: value },
    }));
  }

  function setDobles(rows: DoblesRow[]) {
    setState((prev) => ({ ...prev, dobles: rows }));
  }

  function setCombinada(rows: CombinadaRow[]) {
    setState((prev) => ({ ...prev, combinada: rows }));
  }

  function setTablaCalibre(key: (typeof TABLAS_CALIBRE)[number]["key"], value: TablaCalibreState) {
    setState((prev) => ({
      ...prev,
      tablasCalibre: { ...prev.tablasCalibre, [key]: value },
    }));
  }

  function setTuboRemarco(rows: TablaRow[]) {
    setState((prev) => ({ ...prev, tuboRemarco: rows }));
  }

  function handleSubmit(event: React.FormEvent) {
    event.preventDefault();
    const { fieldErrors: errors, formError: cellsError } = validatePreciosForm(state);
    setFieldErrors(errors);
    setFormError(cellsError);
    if (Object.keys(errors).length > 0 || cellsError !== null) {
      return;
    }
    onSubmit(formStateToBody(state));
  }

  const apiErrorMessage =
    apiError !== null && apiError !== undefined ? adminErrorMessage(apiError) : null;

  return (
    <form className="flex flex-col gap-6" onSubmit={handleSubmit} noValidate>
      {/* Sección 1: lámina e impuestos */}
      <Card>
        <CardHeader>
          <CardTitle>Lámina e impuestos</CardTitle>
        </CardHeader>
        <CardContent className="grid gap-5 sm:grid-cols-2">
          {SECCION_LAMINA.map((key) => (
            <EscalarField
              key={key}
              fieldKey={key}
              value={state.escalares[key]}
              error={fieldErrors[key]}
              onChange={(v) => setEscalar(key, v)}
            />
          ))}
        </CardContent>
      </Card>

      {/* Sección 2: doblez, perfiles y combinadas */}
      <Card>
        <CardHeader>
          <CardTitle>Doblez, perfiles y combinadas</CardTitle>
        </CardHeader>
        <CardContent className="flex flex-col gap-6">
          <div className="grid gap-6 sm:grid-cols-2">
            <div className="flex flex-col gap-5">
              {SECCION_DOBLES_IZQUIERDA.map((key) => (
                <EscalarField
                  key={key}
                  fieldKey={key}
                  value={state.escalares[key]}
                  error={fieldErrors[key]}
                  onChange={(v) => setEscalar(key, v)}
                />
              ))}
              <DoblesCalibresEditor rows={state.dobles} onChange={setDobles} />
            </div>
            <div className="flex flex-col gap-5">
              {SECCION_DOBLES_DERECHA.map((key) => (
                <EscalarField
                  key={key}
                  fieldKey={key}
                  value={state.escalares[key]}
                  error={fieldErrors[key]}
                  onChange={(v) => setEscalar(key, v)}
                />
              ))}
            </div>
          </div>
          <CombinadaMedidasEditor rows={state.combinada} onChange={setCombinada} />
        </CardContent>
      </Card>

      {/* Sección 3: tablas por medida */}
      <Card>
        <CardHeader>
          <CardTitle>Tablas por medida</CardTitle>
        </CardHeader>
        <CardContent className="grid gap-6 lg:grid-cols-2">
          {TABLAS_CALIBRE.map(({ key, title }) => (
            <TablaCalibreEditor
              key={key}
              title={title}
              value={state.tablasCalibre[key]}
              onChange={(value) => setTablaCalibre(key, value)}
            />
          ))}
          <TablaSimpleEditor
            title={TABLA_SIMPLE_TITLE}
            rows={state.tuboRemarco}
            onChange={setTuboRemarco}
          />
        </CardContent>
      </Card>

      {formError && (
        <p role="alert" className="text-sm text-red-600">
          {formError}
        </p>
      )}
      {apiErrorMessage && (
        <p role="alert" className="text-sm text-red-600">
          {apiErrorMessage}
        </p>
      )}
      {isSaved && !apiErrorMessage && (
        <p role="status" className="text-sm text-green-700">
          Precios guardados correctamente.
        </p>
      )}

      <div className="flex items-center gap-2">
        <Button type="submit" variant="primary" size="md" disabled={isSubmitting}>
          {isSubmitting ? "Guardando…" : "Guardar"}
        </Button>
      </div>
    </form>
  );
}

interface EscalarFieldProps {
  fieldKey: PrecioEscalarKey;
  value: string;
  error?: string | undefined;
  onChange: (value: string) => void;
}

/** Campo numérico escalar con etiqueta y error (réplica de los inputs del SSR). */
function EscalarField({ fieldKey, value, error, onChange }: EscalarFieldProps) {
  const inputId = `precio-${fieldKey}`;
  // El SSR usa step 0.001 para factor_utilidad/iva (decimales finos) y 0.01 para
  // el resto (montos en pesos).
  const step = fieldKey === "factor_utilidad" || fieldKey === "iva" ? "0.001" : "0.01";
  return (
    <div className="flex flex-col gap-1">
      <label htmlFor={inputId} className="text-sm font-medium text-slate-700">
        {ESCALAR_LABEL[fieldKey]}
      </label>
      <Input
        id={inputId}
        type="number"
        step={step}
        inputMode="decimal"
        value={value}
        aria-invalid={error ? true : undefined}
        aria-describedby={error ? `${inputId}-error` : undefined}
        onChange={(event) => onChange(event.target.value)}
      />
      {error && (
        <p id={`${inputId}-error`} className="text-sm text-red-600">
          {error}
        </p>
      )}
    </div>
  );
}
